import React, { useEffect, useState } from 'react';
import {
  fetchHostRequests,
  updateRequestStatus,
} from '@/modules/hosting/client/api/hosting.api';
import { sendMessage } from '@/modules/messages/client/api/messages.api';
import Avatar from '@/modules/users/client/components/Avatar.component';
import PropTypes from 'prop-types';
import InvoiceModal from './InvoiceModal.component';
import StripeOnboarding from '@/modules/payment/client/components/StripeOnboarding.component';

export default function Guest({ currentUser }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, upcoming, past, cancelled
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoiceRequest, setSelectedInvoiceRequest] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    async function loadRequests() {
      try {
        setLoading(true);
        const data = await fetchHostRequests(currentUser._id);
        setRequests(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching guest requests:', error);
      } finally {
        setLoading(false);
      }
    }

    if (currentUser?._id) loadRequests();
  }, [currentUser]);

  // Filter requests based on status and search query
  const filteredRequests = requests.filter(request => {
    // Status filter
    if (filter !== 'all') {
      const statusMap = {
        upcoming: ['accepted', 'paid', 'checked_in', 'in_escrow'],
        past: ['completed', 'payout_released'],
        cancelled: ['rejected', 'refunded'],
      };
      if (!statusMap[filter].includes(request.status)) return false;
    }

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const guestName = `${request.guest?.username || ''}`.toLowerCase();
      const location = request.guest?.locationLiving?.toLowerCase() || '';
      return guestName.includes(searchLower) || location.includes(searchLower);
    }

    return true;
  });

  // Pagination logic
  const totalPages = Math.max(
    1,
    Math.ceil(filteredRequests.length / itemsPerPage),
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRequests = filteredRequests.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery, itemsPerPage]);

  // Calculate stats for sidebar
  const stats = {
    total: requests.length,
    upcoming: requests.filter(req =>
      ['accepted', 'paid', 'checked_in', 'in_escrow'].includes(req.status),
    ).length,
    past: requests.filter(req =>
      ['completed', 'payout_released'].includes(req.status),
    ).length,
    cancelled: requests.filter(req =>
      ['rejected', 'refunded'].includes(req.status),
    ).length,
  };

  const getStatusBadgeClass = status => {
    switch (status) {
      case 'accepted':
        return 'border-success text-success bg-success bg-opacity-10';
      case 'paid':
        return 'border-info text-info bg-info bg-opacity-10';
      case 'checked_in':
      case 'in_escrow':
        return 'border-primary text-primary bg-primary bg-opacity-10';
      case 'payout_released':
        return 'border-success text-success bg-success bg-opacity-10';
      case 'pending':
        return 'border-warning text-warning bg-warning bg-opacity-10';
      case 'rejected':
        return 'border-danger text-danger bg-danger bg-opacity-10';
      case 'refunded':
        return 'border-secondary text-secondary bg-secondary bg-opacity-10';
      case 'completed':
        return 'border-secondary text-secondary bg-secondary bg-opacity-10';
      default:
        return 'border-dark text-dark bg-dark bg-opacity-10';
    }
  };

  const getStatusText = status => {
    switch (status) {
      case 'accepted':
        return 'Confirmed';
      case 'paid':
        return 'Paid';
      case 'checked_in':
        return 'Checked In';
      case 'in_escrow':
        return 'In Escrow';
      case 'payout_released':
        return 'Payout Released';
      case 'pending':
        return 'Pending';
      case 'rejected':
        return 'Cancelled';
      case 'refunded':
        return 'Refunded';
      case 'completed':
        return 'Past';
      default:
        return status;
    }
  };

  const handleStatusUpdate = async (newStatus, requestId) => {
    try {
      // Find the current request to get guest info
      const currentRequest = requests.find(req => req._id === requestId);
      if (!currentRequest) {
        alert('Request not found');
        return;
      }

      const response = await updateRequestStatus(requestId, newStatus);
      const data = response.data;
      const request = data.request;

      // Get guest ID from current request (it's already populated)
      const guestId =
        currentRequest.guest?._id ||
        currentRequest.guest ||
        request.guest?._id ||
        request.guest;

      // Update the request in the local state
      setRequests(prevRequests =>
        prevRequests.map(req =>
          req._id === requestId ? { ...req, status: newStatus } : req,
        ),
      );

      // If accepted, send a message to the guest
      if (newStatus === 'accepted' && guestId) {
        const messageContent = `
          <p><b>Hosting Request ${newStatus?.toUpperCase()}</b></p>
          <p>The host has ${newStatus} your request.</p>
          <p>
            <b>Hosting Type:</b> ${currentRequest.type || request.type}<br />
            <b>Check-in Date:</b> ${new Date(
              currentRequest.checkIn || request.checkIn,
            )?.toLocaleDateString('en-GB')}<br />
            <b>Check-out Date:</b> ${new Date(
              currentRequest.checkOut || request.checkOut,
            )?.toLocaleDateString('en-GB')}<br />
            <b>Total Cost:</b> ${
              currentRequest.currency || request.currency || 'EUR'
            }${currentRequest.totalCost || request.totalCost}
          </p>
          ${
            request.checkoutUrl
              ? `<p><a href="${request.checkoutUrl}">Click here to proceed with payment</a></p>`
              : ''
          }
        `;

        try {
          await sendMessage(guestId, messageContent);
        } catch (messageError) {
          // eslint-disable-next-line no-console
          console.error('Error sending message:', messageError);
          // Don't fail the whole operation if message sending fails
        }
      } else if (newStatus === 'rejected' && guestId) {
        // Send rejection message
        const messageContent = `
          <p><b>Hosting Request ${newStatus?.toUpperCase()}</b></p>
          <p>Unfortunately, your hosting request has been ${newStatus}.</p>
          <p>
            <b>Hosting Type:</b> ${currentRequest.type || request.type}<br />
            <b>Check-in Date:</b> ${new Date(
              currentRequest.checkIn || request.checkIn,
            )?.toLocaleDateString('en-GB')}<br />
            <b>Check-out Date:</b> ${new Date(
              currentRequest.checkOut || request.checkOut,
            )?.toLocaleDateString('en-GB')}<br />
          </p>
        `;

        try {
          await sendMessage(guestId, messageContent);
        } catch (messageError) {
          // eslint-disable-next-line no-console
          console.error('Error sending message:', messageError);
          // Don't fail the whole operation if message sending fails
        }
      }

      // If accepted and has checkout URL, redirect to payment
      if (
        newStatus === 'accepted' &&
        request.checkoutUrl &&
        request.host !== currentUser._id
      ) {
        window.location.href = request.checkoutUrl;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating request status:', error);
      alert('Failed to update request status. Please try again.');
    }
  };

  return (
    <div className="container-spacer">
      <div className="container mt-4" style={{ width: 'fit-content' }}>
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex align-items-center mb-3">
              <div className="bg-primary rounded-circle p-3 me-3">
                <i className="fas fa-home text-white fs-4"></i>
              </div>
              <div>
                <h1 className="h2 mb-1 fw-bold text-dark">My Guests</h1>
                {/* <h3 className="mb-1 fw-bold text-primary">
                  {currentUser?.firstName} {currentUser?.lastName}
                </h3> */}
                <p className="text-muted mb-0">
                  Manage your guest requests and hosting interactions
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Main Content Column */}
          <div className="col-lg-8 col-12 mb-4">
            {/* Filters and Search */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light border-end-0 py-3 px-3">
                        <i className="fas fa-filter text-muted"></i>
                      </span>
                      <select
                        className="form-select border-start-0 py-3"
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        style={{
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                        }}
                      >
                        <option value="all">All Status</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="past">Past</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name or location..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={() => {
                        setFilter('all');
                        setSearchQuery('');
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                  ></div>
                  <h5 className="text-muted">Loading guests...</h5>
                  <p className="text-muted small">
                    Please wait while we fetch your hosting requests
                  </p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && filteredRequests.length === 0 && (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-inbox fs-1 text-muted mb-3"></i>
                  <h5 className="text-muted">
                    {searchQuery || filter !== 'all'
                      ? 'No Guests Found'
                      : 'No hosting requests'}
                  </h5>
                  <p className="text-muted mb-0">
                    {searchQuery || filter !== 'all'
                      ? 'Try adjusting your search or filters'
                      : "You don't have any hosting requests yet."}
                  </p>
                </div>
              </div>
            )}

            {/* Requests List */}
            {!loading && filteredRequests.length > 0 && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white py-3">
                  <h5 className="mb-0 fw-bold">
                    <i className="fas fa-list me-2 text-primary"></i>
                    Profiles
                  </h5>
                </div>
                <div className="card-body p-0">
                  {currentRequests.map((request, index) => (
                    <div
                      key={request._id}
                      className={`border-bottom ${
                        index === currentRequests.length - 1
                          ? 'border-bottom-0'
                          : ''
                      } mb-3`}
                    >
                      <div className="p-4">
                        <div className="row align-items-start">
                          {/* Profile Image and Basic Info */}
                          <div className="col-md-2 col-3">
                            <Avatar
                              source="/img/avatar.png"
                              user={request?.guest}
                              size={64}
                            />
                          </div>
                          <div className="col-md-6 col-9">
                            <div className="mb-2">
                              <h6 className="fw-bold mb-1">
                                {request.guest?.username || 'Guest'}{' '}
                              </h6>
                              <div className="d-flex align-items-center text-muted small">
                                <i className="fas fa-star text-warning me-1"></i>
                                <i className="fas fa-map-marker-alt me-1"></i>
                                <span>
                                  {request.guest?.locationLiving ||
                                    'Unknown location'}
                                </span>
                              </div>
                            </div>

                            <div className="d-flex align-items-center text-muted small mb-2">
                              <i className="fas fa-calendar me-2"></i>
                              <span>
                                {new Date(request.checkIn).toLocaleDateString(
                                  'en-GB',
                                )}{' '}
                                →{' '}
                                {new Date(request.checkOut).toLocaleDateString(
                                  'en-GB',
                                )}
                              </span>
                            </div>

                            {/* Message */}
                            {request.message && (
                              <div className="bg-light rounded p-2 mb-2">
                                <p className="mb-0 text-dark small fst-italic">
                                  {request.message}
                                </p>
                              </div>
                            )}

                            {/* Price & Info */}
                            <div className="row mt-2">
                              <div className="col-md-6">
                                <div className="d-flex align-items-center text-muted small mb-1">
                                  <i className="fas fa-tag me-2"></i>
                                  <span>
                                    <strong>Type:</strong> {request.type}
                                  </span>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="d-flex align-items-center text-muted small mb-1">
                                  <i className="fas fa-money-bill-wave me-2"></i>
                                  <span>
                                    <strong>Total:</strong> {request.totalCost}{' '}
                                    {request.currency}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Status and Actions */}
                          <div className="col-md-4 col-12">
                            <div className="d-flex flex-column align-items-end h-100">
                              {/* Status Badge */}
                              <p>
                                <span
                                  className={`badge ${getStatusBadgeClass(
                                    request.status,
                                  )} mb-2 px-3 py-2`}
                                >
                                  {getStatusText(request.status)}
                                </span>
                              </p>

                              {/* Action Buttons */}
                              <div className="d-flex flex-column align-items-end gap-1 mt-auto">
                                {request.status === 'pending' && (
                                  <div className="d-flex flex-column align-items-end gap-0">
                                    <button
                                      className="btn btn-success btn-sm py-1"
                                      onClick={() =>
                                        handleStatusUpdate(
                                          'accepted',
                                          request._id,
                                        )
                                      }
                                      style={{
                                        marginRight: '8px',
                                      }}
                                    >
                                      <i className="fas fa-check me-1"></i>
                                      Accept
                                    </button>
                                    <button
                                      className="btn btn-danger btn-sm py-1"
                                      onClick={() =>
                                        handleStatusUpdate(
                                          'rejected',
                                          request._id,
                                        )
                                      }
                                    >
                                      <i className="fas fa-times me-1"></i>
                                      Reject
                                    </button>
                                  </div>
                                )}
                                {(request.status === 'paid' ||
                                  request.paymentStatus === 'paid') && (
                                  <div className="text-muted small text-end mb-2">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Waiting for guest check-in
                                  </div>
                                )}
                                {request.status === 'in_escrow' && (
                                  <div className="text-muted small text-end mb-2">
                                    <i className="fas fa-lock me-1"></i>
                                    Funds in escrow
                                    {request.escrowReleaseDate && (
                                      <div className="mt-1">
                                        Release:{' '}
                                        {new Date(
                                          request.escrowReleaseDate,
                                        ).toLocaleDateString('en-GB')}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {request.status === 'payout_released' && (
                                  <div className="text-success small text-end mb-2">
                                    <i className="fas fa-check-circle me-1"></i>
                                    Payout released
                                  </div>
                                )}
                                {request.status === 'refunded' && (
                                  <div className="text-muted small text-end mb-2">
                                    <i className="fas fa-undo me-1"></i>
                                    Refunded
                                  </div>
                                )}
                                <a
                                  href={`/messages/${request.guest?.username}?userId=${request.guest?._id}&requestId=${request._id}`}
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  <i className="fas fa-comment me-1"></i>
                                  Message
                                </a>
                                {request.invoice && (
                                  <button
                                    className="btn btn-link text-dark fw-bold text-decoration-none"
                                    style={{
                                      fontSize: '1.1rem',
                                      backgroundColor: '#f8f9fa',
                                      padding: '0.375rem 0.75rem',
                                      borderRadius: '0.25rem',
                                      border: '1px solid #dee2e6',
                                    }}
                                    onClick={() => {
                                      setSelectedInvoiceRequest(request);
                                      setShowInvoiceModal(true);
                                    }}
                                  >
                                    Invoice
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination */}
                {filteredRequests.length > 0 && (
                  <div className="card-footer bg-white border-top">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-3">
                        <div className="text-muted small">
                          Showing {indexOfFirstItem + 1} to{' '}
                          {Math.min(indexOfLastItem, filteredRequests.length)}{' '}
                          of {filteredRequests.length} result
                          {filteredRequests.length !== 1 ? 's' : ''}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <label className="text-muted small mb-0">Show:</label>
                          <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={itemsPerPage}
                            onChange={e => {
                              setItemsPerPage(Number(e.target.value));
                              setCurrentPage(1);
                            }}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                          </select>
                        </div>
                      </div>
                      <nav aria-label="Page navigation">
                        <ul className="pagination mb-0" style={{ gap: '4px' }}>
                          <li
                            className={`page-item ${
                              currentPage === 1 ? 'disabled' : ''
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              style={{
                                minWidth: '90px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                backgroundColor:
                                  currentPage === 1 ? '#f8f9fa' : '#fff',
                                color:
                                  currentPage === 1 ? '#6c757d' : '#495057',
                                cursor:
                                  currentPage === 1 ? 'not-allowed' : 'pointer',
                              }}
                              onMouseEnter={e => {
                                if (currentPage !== 1) {
                                  e.target.style.backgroundColor = '#e9ecef';
                                  e.target.style.borderColor = '#adb5bd';
                                }
                              }}
                              onMouseLeave={e => {
                                if (currentPage !== 1) {
                                  e.target.style.backgroundColor = '#fff';
                                  e.target.style.borderColor = '#dee2e6';
                                }
                              }}
                            >
                              <i className="fas fa-chevron-left me-1"></i>
                              Previous
                            </button>
                          </li>
                          {totalPages > 1 &&
                            Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                // Show first page, last page, current page, and pages around current
                                return (
                                  page === 1 ||
                                  page === totalPages ||
                                  (page >= currentPage - 1 &&
                                    page <= currentPage + 1)
                                );
                              })
                              .map((page, index, array) => {
                                // Add ellipsis if needed
                                const showEllipsisBefore =
                                  index > 0 && array[index - 1] < page - 1;
                                const isActive = currentPage === page;
                                return (
                                  <React.Fragment key={page}>
                                    {showEllipsisBefore && (
                                      <li className="page-item disabled">
                                        <span
                                          className="page-link"
                                          style={{
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            color: '#6c757d',
                                            padding: '8px 12px',
                                          }}
                                        >
                                          ...
                                        </span>
                                      </li>
                                    )}
                                    <li
                                      className={`page-item ${
                                        isActive ? 'active' : ''
                                      }`}
                                    >
                                      <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(page)}
                                        style={{
                                          minWidth: '40px',
                                          border: isActive
                                            ? '1px solid #0d6efd'
                                            : '1px solid #dee2e6',
                                          borderRadius: '6px',
                                          padding: '8px 12px',
                                          fontWeight: isActive ? '600' : '500',
                                          transition: 'all 0.2s ease',
                                          backgroundColor: isActive
                                            ? '#0d6efd'
                                            : '#fff',
                                          color: isActive ? '#fff' : '#495057',
                                          cursor: 'pointer',
                                        }}
                                        onMouseEnter={e => {
                                          if (!isActive) {
                                            e.target.style.backgroundColor =
                                              '#e9ecef';
                                            e.target.style.borderColor =
                                              '#adb5bd';
                                          }
                                        }}
                                        onMouseLeave={e => {
                                          if (!isActive) {
                                            e.target.style.backgroundColor =
                                              '#fff';
                                            e.target.style.borderColor =
                                              '#dee2e6';
                                          }
                                        }}
                                      >
                                        {page}
                                      </button>
                                    </li>
                                  </React.Fragment>
                                );
                              })}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? 'disabled' : ''
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              style={{
                                minWidth: '90px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                backgroundColor:
                                  currentPage === totalPages
                                    ? '#f8f9fa'
                                    : '#fff',
                                color:
                                  currentPage === totalPages
                                    ? '#6c757d'
                                    : '#495057',
                                cursor:
                                  currentPage === totalPages
                                    ? 'not-allowed'
                                    : 'pointer',
                              }}
                              onMouseEnter={e => {
                                if (currentPage !== totalPages) {
                                  e.target.style.backgroundColor = '#e9ecef';
                                  e.target.style.borderColor = '#adb5bd';
                                }
                              }}
                              onMouseLeave={e => {
                                if (currentPage !== totalPages) {
                                  e.target.style.backgroundColor = '#fff';
                                  e.target.style.borderColor = '#dee2e6';
                                }
                              }}
                            >
                              Next
                              <i className="fas fa-chevron-right ms-1"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar Column */}
          <div className="col-lg-4 col-12">
            {/* Stripe Connect Onboarding */}
            <div className="mb-4">
              <StripeOnboarding
                currentUser={currentUser}
                onComplete={() => {
                  // Refresh account status if needed
                  window.location.reload();
                }}
              />
            </div>

            {/* Quick Stats Card */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-bold">
                  <i className="fas fa-chart-bar me-2 text-primary"></i>
                  Quick Stats
                </h6>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">Total guests hosted: </span>
                  <span className="fw-bold">{stats.total}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">Upcoming stays: </span>
                  <span className="fw-bold text-success">{stats.upcoming}</span>
                </div>
                {/* <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">Average rating </span>
                  <span className="fw-bold text-warning">4.8</span>
                </div> */}
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Response rate: </span>
                  <span className="fw-bold text-success">
                    {stats.total > 0
                      ? `${Math.round(
                          ((stats.upcoming + stats.past) / stats.total) * 100,
                        )}%`
                      : '0%'}
                  </span>
                </div>
              </div>
            </div>

            {/* Host Tips Card */}
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">
                  <i className="fas fa-lightbulb me-2 text-warning"></i>
                  Host Tips
                </h6>
                <p className="small text-muted mb-3">
                  Prepare guest room and local recommendations in advance for
                  better hosting experience.
                </p>
                <div className="d-flex align-items-center text-muted small">
                  <i className="fas fa-clock me-2"></i>
                  <span>
                    Last updated: {new Date().toLocaleDateString('en-GB')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      <InvoiceModal
        show={showInvoiceModal}
        onHide={() => {
          setShowInvoiceModal(false);
          setSelectedInvoiceRequest(null);
        }}
        request={selectedInvoiceRequest}
        currentUser={currentUser}
      />
    </div>
  );
}

Guest.propTypes = {
  currentUser: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    username: PropTypes.string,
    locationLiving: PropTypes.string,
    avatarUploaded: PropTypes.bool,
    emailHash: PropTypes.string,
  }).isRequired,
};
